import React, { createContext, useContext, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { fetchMappingData } from '../../../store/reducers/feeManagement/feeManagementSlice'
import {
  selectMappingData,
  selectMappingDataLoading,
  selectMappingDataError,
} from '../../../store/reducers/feeManagement/feeManagementSelectors'
import { ALL_ENTITY_TYPES } from '../constants/OverrideTypes'

// Create the context
const GridDataContext = createContext(null)

// Custom hook to use the context
export const useGridData = () => {
  const context = useContext(GridDataContext)
  if (!context) {
    throw new Error('useGridData must be used within a GridDataProvider')
  }
  return context
}

// Provider component
export const GridDataProvider = ({ children }) => {
  const dispatch = useDispatch()

  // Get mapping data from Redux state using selectors
  const mappingData = useSelector(selectMappingData)
  const isLoadingMappingData = useSelector(selectMappingDataLoading)
  const mappingDataError = useSelector(selectMappingDataError)

  // Function to fetch mapping data based on entity types
  const fetchMappingDataFn = useCallback(
    async (entityTypes) => {
      if (!entityTypes || !entityTypes.length) {
        return
      }

      dispatch(fetchMappingData(entityTypes))
    },
    [dispatch],
  )

  // Refetch all mapping data every time the provider mounts. This provider is
  // scoped to the /fee-management routes, so it remounts each time the user
  // enters Fee Management. The cache lives in Redux for the whole SPA session,
  // so gating on "is it empty?" (as this once did) skips the refetch and lets a
  // stale cache reject products/branches/vendors that were created or renamed
  // elsewhere in the app. fetchMappingDataFn is referentially stable, so this
  // runs once per mount.
  useEffect(() => {
    const uniqueEntityTypes = [...new Set(ALL_ENTITY_TYPES)]
    fetchMappingDataFn(uniqueEntityTypes)
  }, [fetchMappingDataFn])

  // Expose the context value
  const value = {
    mappingData,
    isLoadingMappingData,
    mappingDataError,
    fetchMappingData: fetchMappingDataFn,
  }

  return <GridDataContext.Provider value={value}>{children}</GridDataContext.Provider>
}

GridDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default GridDataContext
